import { useMemo } from 'react';

import { ActionIcon, Divider, Group, Stack, Text } from '@mantine/core';

import { IconSelector, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import type { VaultPropertyOption } from '../../../../../../../../shared/entities/VaultPropertyOption';
import {
  CheckBoxList,
  CheckboxItemWithChildren,
  DropdownFooter,
} from '../../../../../checkBoxList/CheckBoxList';
import { useGetVaultPropertiesQuery } from '../../../../../vault/hooks/vaultProperty.hook';
import { Pills } from '../pills/pills';

type PropertiesFieldProps = {
  selectedProperties: number[];
  onPropertiesChange: (properties: number[]) => void;
};

interface SelectedProperty extends VaultPropertyOption {
  color?: string;
}

export function PropertiesField({
  selectedProperties,
  onPropertiesChange,
}: PropertiesFieldProps) {
  const { data: properties } = useGetVaultPropertiesQuery();

  const checkBoxData =
    properties.map(property => ({
      value: property.id.toString(),
      label: property.name,
      children: property.options.map(option => ({
        value: option.id.toString(),
        color: property.color,
        label: (
          <Pills customColor={property.color} shape="round">
            {option.name}
          </Pills>
        ),
      })),
    })) ?? [];

  const valuesAsStrings = selectedProperties?.map(id => id.toString()) ?? [];

  const handleChange = (value: string[]) => {
    onPropertiesChange(value.map(Number));
  };

  const onReset = () => {
    onPropertiesChange([]);
  };

  const onRemoveProperty = (propertyId: number) => {
    onPropertiesChange(selectedProperties.filter(id => id !== propertyId));
  };

  const selectedPropertiesData: SelectedProperty[] = useMemo(() => {
    return selectedProperties
      .map(id => {
        const property = properties.find(property =>
          property.options.some(option => option.id === id),
        );

        const option = property?.options.find(option => option.id === id);

        if (!property || !option) {
          return undefined;
        }

        return {
          color: property.color,
          ...option,
        };
      })
      .filter(Boolean) as SelectedProperty[];
  }, [properties, selectedProperties]);

  return (
    <CheckBoxList
      label="Propriétés"
      count={valuesAsStrings.length}
      popoverWidth="target"
      withinPortal={true}
      customTarget={
        <Target
          selectedProperties={selectedPropertiesData}
          onRemoveProperty={onRemoveProperty}
        />
      }
    >
      <Stack spacing={0} mah={300} style={{ overflowY: 'auto' }}>
        {checkBoxData.map(item => (
          <CheckboxItemWithChildren
            key={item.value}
            checkBoxData={item}
            values={valuesAsStrings}
            onChange={handleChange}
          />
        ))}
      </Stack>
      <Stack
        pos="absolute"
        bottom={'0'}
        left={8}
        spacing="02"
        p="02"
        w="calc(100% - 16px)"
        bg="white"
      >
        <Divider color="gray.1" />
        <DropdownFooter onReset={onReset} />
      </Stack>
    </CheckBoxList>
  );
}

interface TargetProps {
  selectedProperties: SelectedProperty[];
  onRemoveProperty: (propertyId: number) => void;
}

function Target({ selectedProperties, onRemoveProperty }: TargetProps) {
  const { t } = useTranslation('bidResponse');

  function handleRemove(
    e: React.MouseEvent<HTMLButtonElement>,
    propertyId: number,
  ) {
    e.stopPropagation();
    onRemoveProperty(propertyId);
  }

  return (
    <Stack spacing="02">
      <Text size="sm" fw={500}>
        {t('project.createOrEditProject.propertiesField.label')}
      </Text>
      <Group
        spacing="03"
        py="02"
        px="03"
        noWrap
        align="center"
        position="apart"
        sx={theme => ({
          border: `1px solid ${theme.colors.gray[2]}`,
          borderRadius: theme.radius.md,
          cursor: 'pointer',
        })}
      >
        <Group spacing="03" style={{ flex: 1 }}>
          {selectedProperties?.map(property => (
            <Pills
              key={property.id}
              customColor={property.color}
              endContent={
                <ActionIcon
                  variant="transparent"
                  size="xs"
                  onClick={e => handleRemove(e, property.id)}
                >
                  <IconX size={16} />
                </ActionIcon>
              }
            >
              {property.name}
            </Pills>
          ))}
          <Text size="sm" color="gray.4" sx={{ pointerEvents: 'none' }}>
            {t('project.createOrEditProject.propertiesField.placeholder')}
          </Text>
        </Group>
        <IconSelector size={16} />
      </Group>
    </Stack>
  );
}
